import { Box, Container } from '@mui/material'
import { useEffect, useMemo, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useLocation, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next' // LAP-733 (GE022)
import CustomButton from '../../Components/CustomButton/CustomButton'
import { Input } from '../../Components/Input'
import ComboBox from '../../Components/ComboBox'
import { makeGetRequest, makePostRequest } from '../../utils/api'
import urls from '../../utils/urls'
import ManageCategory from './CreateCategories/Index'
import CreateOperationForm from './CreateContact'
import ManageGroup from './CreateGroup/Index'
import ModifierCreateForm from './EditContact'
import ListOperations from './ListContacts'
import CircularProgress from '@mui/material/CircularProgress'
import AddConnection from 'Components/UserOptionCard/AddConnection'
import CreateMessage from 'containers/MyInbox/CreateMessage'
import UploadOperations from './UploadOperations'

export const MesContacts = () => {
  const { t } = useTranslation('layout') // LAP-733 (GE022)
  const [mOpen, setMOpen] = useState(false)
  const navigate = useNavigate()
  const location = useLocation()
  const searchParams = new URLSearchParams(location.search)
  const [openImportDialog, setOpenImportDialog] = useState(searchParams.get('openImport') === '1')

  const [operations, setOperations] = useState({})
  const [selectedRows, setSelectedRows] = useState([])

  const [addGooglContact, setAddGooglContact] = useState(false)
  // Type pré-sélectionné dans AddConnection quand un compte précis doit être
  // reconnecté (ex. 'iCloud' — pas de flux OAuth, formulaire dédié).
  const [reconnectType, setReconnectType] = useState(null)
  const [loading, setLoading] = useState(false)
  
  // Filter dropdown options
  const [titres] = useState([
    { id: 1, label: 'Mme' },
    { id: 2, label: 'Mr' },
    { id: 3, label: 'Mlle' },
    { id: 4, label: 'Dr' },
    { id: 5, label: 'ST' },
  ])
  const [countries, setCountries] = useState([])
  const [groups, setGroups] = useState([])
  const [categories, setCategories] = useState([])
  const [sousCategories, setSousCategories] = useState([])
  
  const [filterValues, setFilterValues] = useState({
    Titre: null,
    Pays: null,
    Groups: null,
    Categorie: null,
    SousCategorie: null,
  })
  
  const { handleSubmit, control, reset } = useForm({
    defaultValues: {
      Titre: '',
      Nom: '',
      Prenom: '',
      surnom: '',
      Job: '',
      Societe: '',
      Pays: '',
      Portable: '',
      Ville: '',
      Groups: '',
      SousCategorie: '',
      Categorie: '',
    },
  })
  // Signature des filtres : on ignore `cid` (ouverture d'une fiche) pour ne
  // PAS recharger la liste — et perdre le scroll infini — quand on ouvre un
  // contact.
  const filterSignature = useMemo(() => {
    const p = new URLSearchParams(location.search)
    p.delete('cid')
    return p.toString()
  }, [location.search])

  useEffect(() => {
    fetchFilterOptions()
  }, [])
  useEffect(() => {
    fetchOperations(`${filterSignature ? filterSignature + '&' : ''}limit=30&offset=0`)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterSignature])
  // Open import dialog when arriving from onboarding with ?openImport=1
  useEffect(() => {
    if (searchParams.get('openImport') === '1') {
      setOpenImportDialog(true)
    }
  }, [location.search])

  const fetchFilterOptions = async () => {
    try {
      // Fetch countries
      const countriesRes = await makeGetRequest('/api/Social/get-countries/', {}, true)
      if (countriesRes.status === 200) {
        const filterPays = countriesRes.data.map((v) => ({ label: v.CountryCode, id: v.id }))
        setCountries(filterPays)
      }

      // Fetch groups
      const groupsRes = await makeGetRequest('/api/Social/get-groups/', {}, true)
      if (groupsRes.status === 200) {
        const filterGroups = groupsRes.data.map((v) => ({ label: v.Name, id: v.id }))
        setGroups(filterGroups)
      }

      // Fetch categories
      const categoriesRes = await makeGetRequest('/api/Social/get-categories/', {}, true)
      if (categoriesRes.status === 200) {
        const filterCategories = categoriesRes.data.Categorie.map((v) => ({
          label: v.Categorie,
          id: v.id,
        }))
        const filterSousCategories = categoriesRes.data.SousCategorie.map((v) => ({
          label: v.SousCategorie,
          id: v.id,
          category: v.Categorie,
        }))
        setCategories(filterCategories)
        setSousCategories(filterSousCategories)
      }
    } catch (error) {
      console.error('Error fetching filter options:', error)
    }
  }

  const fetchOperations = async (pagination = 'limit=30&offset=0', append = false) => {
    setLoading(true)
    let url = `${urls.crudSocialDashboardDetailsOperations}`
    if (pagination !== '') {
      url += '?' + pagination
    }
    const response = await makeGetRequest(url, {}, true)
    if (response.status === 200) {
      setOperations((prev) =>
        append && prev && prev.results
          ? { ...response.data, results: [...prev.results, ...(response.data.results || [])] }
          : { ...response.data },
      )
    }
    setLoading(false)
  }
  const syncContacts = async () => {
    setLoading(true)
    const res = await makeGetRequest('/api/user/sync-connectes/', {}, true)
    setLoading(false)

    // 201 = aucun compte actif : on propose directement d'en (re)connecter un.
    if (res?.status === 201) {
      setReconnectType(null)
      setAddGooglContact(true)
      return
    }

    if (res?.status === 200) {
      fetchOperations()
      const reconnect = res.data?.reconnect || []
      if (reconnect.length > 0) {
        // Un compte a été archivé pendant la synchro (identifiants invalides,
        // etc.) : on le dit et on rouvre le formulaire de connexion,
        // pré-réglé si un seul type de compte est concerné.
        alert(
          t('contacts.syncReconnect', {
            accounts: reconnect.map((a) => a.name).join(', '),
          }),
        )
        const types = [...new Set(reconnect.map((a) => a.acc_type))]
        setReconnectType(types.length === 1 ? types[0] : null)
        setAddGooglContact(true)
      } else {
        const nb = res.data?.i_contacts ?? 0
        if (nb > 0) {
          alert(t('contacts.syncSuccess', { count: nb }))
        } else if (window.confirm(t('contacts.syncNoneOfferConnect'))) {
          // Cul-de-sac historique : après suppression d'une source, tant
          // qu'un AUTRE compte restait actif, sync renvoyait 200/0 (jamais
          // 201) et le popup de connexion ne s'ouvrait plus. On le propose
          // donc explicitement ici.
          setReconnectType(null)
          setAddGooglContact(true)
        }
      }
      return
    }

    // Toute autre issue (500, réseau...) était silencieuse : le bouton
    // semblait "mort". On affiche l'erreur backend ou un message générique.
    alert(res?.data?.error || t('contacts.syncError'))
  }
  const closeOpen = () => {
    setAddGooglContact(false)
    setReconnectType(null)
    // Un compte vient peut-être d'être (re)connecté avec import de contacts :
    // sans refetch, les nouveaux contacts n'apparaissaient qu'après F5.
    fetchOperations()
  }

  // Common field styles
  const fieldStyle = {
    '& .MuiAutocomplete-paper': {
      boxShadow: 'none',
    },
    '& .MuiAutocomplete-listbox': {
      boxShadow: 'none',
    },
    '& .MuiInputBase-root': {
      backgroundColor: 'rgba(255, 255, 255, 0.95)',
      borderRadius: '8px',
      height: '56px',
      border: 'none',
      boxShadow: 'none',
      '&:hover': {
        backgroundColor: '#ffffff',
        boxShadow: 'none',
      },
      '&.Mui-focused': {
        backgroundColor: '#ffffff',
        boxShadow: 'none',
        border: 'none',
      },
      '& fieldset': {
        border: 'none',
      },
    },
    '& .MuiFilledInput-root': {
      backgroundColor: 'rgba(255, 255, 255, 0.95)',
      borderRadius: '8px',
      height: '56px',
      border: 'none',
      boxShadow: 'none',
      '&:hover': {
        backgroundColor: '#ffffff',
        boxShadow: 'none',
      },
      '&.Mui-focused': {
        backgroundColor: '#ffffff',
        boxShadow: 'none',
        border: 'none',
      },
      '&:before, &:after': {
        display: 'none',
      },
    },
    '& .MuiOutlinedInput-root': {
      '& fieldset': {
        border: 'none',
      },
      '&:hover fieldset': {
        border: 'none',
      },
      '&.Mui-focused fieldset': {
        border: 'none',
      },
    },
    '& .MuiInputLabel-root': {
      color: 'rgba(0, 27, 66, 0.7)',
      fontWeight: 500,
      '&.Mui-focused': {
        color: '#1976d2',
      },
    },
    '& .MuiAutocomplete-endAdornment': {
      '& .MuiIconButton-root': {
        color: 'rgba(0, 27, 66, 0.7)',
      },
    },
  }
  const filterOperations = (data) => {
    const params = {}
    
    // Handle FK/M2M fields - send IDs
    if (filterValues.Titre) params.Titre = filterValues.Titre.id
    if (filterValues.Pays) params.Pays = filterValues.Pays.id
    if (filterValues.Groups) params.Groups = filterValues.Groups.id
    if (filterValues.Categorie) params.Categorie = filterValues.Categorie.id
    if (filterValues.SousCategorie) params.SousCategorie = filterValues.SousCategorie.id
    
    // Handle text fields
    if (data.Nom) params.Nom = data.Nom
    if (data.Prenom) params.Prenom = data.Prenom
    if (data.surnom) params.surnom = data.surnom
    if (data.Job) params.Job = data.Job
    if (data.Societe) params.Societe = data.Societe
    if (data.Portable) params.Portable = data.Portable
    if (data.Ville) params.Ville = data.Ville
    
    const queryParams = new URLSearchParams(params).toString()
    navigate(`?${queryParams}`)
  }

  const clearFilters = () => {
    // Reset dropdown filters
    setFilterValues({
      Titre: null,
      Pays: null,
      Groups: null,
      Categorie: null,
      SousCategorie: null,
    })
    // Reset text field filters
    reset()
    navigate('')
    fetchOperations()
  }

  const deleteBulkOperations = async () => {
    if (selectedRows.length === 0) {
      // LAP-733 (GE022)
      alert(t('contacts.alertSelectOne'))
    } else {
      // const filteredOperations = operations.results.filter(
      //     ( v ) => selectedRows.indexOf( v.id ) === -1
      // );
      // setOperations( {
      //     ...operations,
      //     results: filteredOperations,
      //     count: operations.count - selectedRows.length,
      // } );
      fetchOperations()
      const response = await makePostRequest(
        urls.deleteBulkOperationsSocial,
        { selected: selectedRows },
        {},
        true,
      )
      if (response.status === 200) {
        console.log('Deleted Successfully')
      }
    }
    fetchOperations()
  }
  return (
    <>
      {mOpen && (
        <CreateMessage
          setMOpen={setMOpen}
          /* LAP-733 (GE022) */
          title={t('contacts.shareTitle')}
          settingId={12}
          contact={operations.results.filter((v) => v.id === selectedRows[0])[0]}
        />
      )}
      {addGooglContact && (
        <AddConnection
          updateList={closeOpen}
          openM={true}
          initialType={reconnectType}
          showTrigger={false}
        />
      )}
      <Box bgcolor={'#001b42'} p={3}>
        <Container maxWidth="xl">
          <form onSubmit={handleSubmit(filterOperations)}>
            <Box
              display={'flex'}
              flexWrap="wrap"
              alignItems="center"
              gap={2}
              sx={{
                '& .MuiTextField-root': {
                  width: '200px',
                  height: '56px',
                },
                '& .MuiAutocomplete-root': {
                  width: '200px',
                  height: '56px',
                },
                '& .MuiInputBase-root': {
                  height: '56px',
                },
              }}
            >
              {/* LAP-733 (GE022) — all filter labels go through i18next now. */}
              <ComboBox
                handleAutoComplete={(name, value) => {
                  setFilterValues((prev) => ({ ...prev, Titre: value }))
                }}
                label={t('contacts.labelTitre')}
                data={titres}
                name="Titre"
                selectedValue={filterValues.Titre}
                sx={fieldStyle}
              />

              <Input
                control={control}
                name="Nom"
                label={t('contacts.labelNom')}
                inputProps={{
                  variant: 'filled',
                  type: 'text',
                  size: 'small',
                }}
                sx={fieldStyle}
                rules={{ required: false }}
              />

              <Input
                size="small"
                name="Prenom"
                label={t('contacts.labelPrenom')}
                control={control}
                inputProps={{
                  variant: 'filled',
                  type: 'text',
                  size: 'small',
                }}
                sx={fieldStyle}
                rules={{ required: false }}
              />

              <Input
                size="small"
                control={control}
                inputProps={{ variant: 'filled', size: 'small' }}
                rules={{ required: false }}
                name="Job"
                label={t('contacts.labelJob')}
                sx={fieldStyle}
              />

              <Input
                size="small"
                control={control}
                inputProps={{
                  variant: 'filled',
                  size: 'small',
                }}
                rules={{ required: false }}
                name="Societe"
                label={t('contacts.labelSociete')}
                sx={fieldStyle}
              />

              <ComboBox
                handleAutoComplete={(name, value) => {
                  setFilterValues((prev) => ({ ...prev, Pays: value }))
                }}
                label={t('contacts.labelPays')}
                data={countries}
                name="Pays"
                selectedValue={filterValues.Pays}
                sx={fieldStyle}
              />

              <Input
                size="small"
                control={control}
                inputProps={{
                  variant: 'filled',
                  size: 'small',
                }}
                rules={{ required: false }}
                name="Portable"
                label={t('contacts.labelPortable')}
                sx={fieldStyle}
              />

              <Input
                size="small"
                control={control}
                inputProps={{
                  variant: 'filled',
                  size: 'small',
                }}
                rules={{ required: false }}
                name="Ville"
                label={t('contacts.labelVille')}
                sx={fieldStyle}
              />

              <ComboBox
                handleAutoComplete={(name, value) => {
                  setFilterValues((prev) => ({ ...prev, Groups: value }))
                }}
                label={t('contacts.labelGroups')}
                data={groups}
                name="Groups"
                selectedValue={filterValues.Groups}
                sx={fieldStyle}
              />

              <ComboBox
                handleAutoComplete={(name, value) => {
                  setFilterValues((prev) => ({ ...prev, Categorie: value }))
                  // Clear sous-categorie when categorie changes
                  setFilterValues((prev) => ({ ...prev, SousCategorie: null }))
                }}
                label={t('contacts.labelCategorie')}
                data={categories}
                name="Categorie"
                selectedValue={filterValues.Categorie}
                sx={fieldStyle}
              />

              <ComboBox
                handleAutoComplete={(name, value) => {
                  setFilterValues((prev) => ({ ...prev, SousCategorie: value }))
                }}
                label={t('contacts.labelSubCategorie')}
                data={
                  filterValues.Categorie
                    ? sousCategories.filter((sc) => sc.category === filterValues.Categorie.label)
                    : sousCategories
                }
                name="SousCategorie"
                selectedValue={filterValues.SousCategorie}
                sx={fieldStyle}
              />

              <Box display="flex" gap={2} sx={{ mt: { xs: 1, sm: 0 } }}>
                <CustomButton
                  type="submit"
                  sx={{
                    width: '120px',
                    height: '56px',
                    background: 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)',
                    color: '#ffffff',
                    fontWeight: 600,
                    borderRadius: '8px',
                    textTransform: 'none',
                    fontSize: '0.95rem',
                    boxShadow: '0 4px 12px rgba(25, 118, 210, 0.3)',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      background: 'linear-gradient(135deg, #1565c0 0%, #0d47a1 100%)',
                      boxShadow: '0 6px 16px rgba(25, 118, 210, 0.4)',
                      transform: 'translateY(-2px)',
                    },
                    '&:active': {
                      transform: 'translateY(0)',
                    },
                  }}
                >
                  {/* LAP-733 (GE022) */}
                  🔍 {t('contacts.btnFilter')}
                </CustomButton>
                <CustomButton
                  type="button"
                  onClick={clearFilters}
                  sx={{
                    width: '120px',
                    height: '56px',
                    background: 'rgba(255, 255, 255, 0.15)',
                    color: '#ffffff',
                    fontWeight: 600,
                    borderRadius: '8px',
                    textTransform: 'none',
                    fontSize: '0.95rem',
                    border: '1px solid rgba(255, 255, 255, 0.3)',
                    backdropFilter: 'blur(10px)',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      background: 'rgba(255, 255, 255, 0.25)',
                      borderColor: 'rgba(255, 255, 255, 0.5)',
                      transform: 'translateY(-2px)',
                    },
                    '&:active': {
                      transform: 'translateY(0)',
                    },
                  }}
                >
                  {/* LAP-733 (GE022) */}
                  ✕ {t('contacts.btnClear')}
                </CustomButton>
              </Box>
            </Box>
          </form>
        </Container>
      </Box>
      {/* <div> */}
      <Container maxWidth="xl">
        <Box justifyContent="flex-end" padding={2} display="flex">
          <Box>
            <CustomButton
              onClick={() => {
                if (selectedRows.length === 1) {
                  setMOpen(true)
                } else {
                  // LAP-733 (GE022)
                  alert(t('contacts.alertSelectOneToShare'))
                }
              }}
            >
              {/* LAP-733 (GE022) */}
              {t('contacts.btnShare')}
            </CustomButton>
            {/* Deux actions distinctes : « Connecter » crée/recrée une source
                (Google, Microsoft, iCloud), « Sync » rafraîchit l'import des
                sources déjà connectées. */}
            <CustomButton
              onClick={() => {
                setReconnectType(null)
                setAddGooglContact(true)
              }}
            >
              {t('contacts.btnConnect')}
            </CustomButton>
            {loading ? (
              <CustomButton>
                <CircularProgress size={20} />
              </CustomButton>
            ) : (
              <CustomButton onClick={syncContacts}>{t('contacts.btnSync')}</CustomButton>
            )}
            <CreateOperationForm setOperations={setOperations} fetchOperations={fetchOperations} />
            <UploadOperations
              update={fetchOperations}
              openFromWizard={openImportDialog}
              onCloseImport={() => {
                setOpenImportDialog(false)
                if (location.search.includes('openImport=1')) {
                  navigate('/MesContacts')
                }
              }}
            />

            <ModifierCreateForm
              fetchOperations={fetchOperations}
              ID={selectedRows.length === 0 ? 0 : selectedRows[0]}
            />
            <ManageGroup />
            <ManageCategory />
            {/* LAP-733 (GE022) */}
            <CustomButton onClick={deleteBulkOperations}>{t('contacts.btnDelete')}</CustomButton>
          </Box>
        </Box>

        <ListOperations
          fetchOperations={fetchOperations}
          setSelectedRows={setSelectedRows}
          operations={operations}
          setOperations={setOperations}
          loading={loading}
        />
      </Container>
    </>
  )
}
